import { zodFallback } from '../../zod/fallback';
import { DsiInspectionResult } from '../dsi/dsiInspection';
import { AreaModel } from '../navigation/areaModel';
import { ClassificationClassName } from '../snapshot/classificationClass';
import { z } from 'zod';

export type UnknownBarcodeItem = z.infer<typeof UnknownBarcodeItem>;
export type BottleBundleCheckItem = z.infer<typeof BottleBundleCheckItem>;
export type ImageDetectionItem = z.infer<typeof ImageDetectionItem>;
export type ImageDetectionsItem = z.infer<typeof ImageDetectionsItem>;
export type RiskAreaItem = z.infer<typeof RiskAreaItem>;
export type RiskAreasItem = z.infer<typeof RiskAreasItem>;
export type VerificationItemProperties = z.infer<typeof VerificationItemProperties>;
export type VerificationItem = z.infer<typeof VerificationItem>;
export type WeightWithoutScanItem = z.infer<typeof WeightWithoutScanItem>;

export const UnknownBarcodeItem = z.object({
    code: z.string(),
    codeType: z.string().nullish(),
    itemCounts: z.number(),
    nextItem: z.string().nullish(),
    prevItem: z.string().nullish(),
    location: z.string().nullish(),
});

export const BottleBundleCheckItem = z.object({
    itemCounts: z.number().nullish(),
    item: z.string().nullish(),
    name: z.string().nullish(),
    topImage: z.string().nullish(),
    bottomImage: z.string().nullish(),
});

export const ImageDetectionItem = z.object({
    cgLongIds: z.array(z.string()).nullish(),
    classifierName: z.string().nullish(),
    probabilityThreshold: z.number().nullish(),
});

export const ImageDetectionsItem = z.object({
    classifierName: ClassificationClassName,
    probabilityThreshold: z.number(),
    confirmation: DsiInspectionResult.nullish(),
    confirmedOn: z.coerce.date().nullish(),
    confirmedBy: z
        .object({
            accountGuid: z.string().nullish(),
            userName: z.string().nullish(),
        })
        .nullish(),
    cashierConfirmed: z.boolean().nullish(),
});

export const WeightWithoutScanInnerItem = z.object({
    reason: z.string().nullish(),
    snapshotPath: z.string().nullish(),
    timestamp: z.coerce.date().nullish(),
});
export type WeightWithoutScanInnerItem = z.infer<typeof WeightWithoutScanInnerItem>;

export const WeightWithoutScanItem = z.object({
    items: WeightWithoutScanInnerItem.array().nullish(),
});

export const RiskAreaItem = z.object({
    area: AreaModel,
    commodityGroupIds: z.array(z.string()).nullish(),
    peculiarities: z.array(z.string()).nullish(),
});

export const RiskAreasItem = z.object({
    areas: RiskAreaItem.array().nullish(),
    commodityGroupIds: z.array(z.string()).nullish(),
    peculiarities: z.array(z.string()).nullish(),
});

export const VerificationItemProperties = z.object({
    age: z.number().nullish(),
    codes: z.array(z.string()).nullish(),
    message: z.string().nullish(),
    code: z.string().nullish(),
    codeType: z.string().nullish(),
    itemNames: z.array(z.string()).nullish(),
    localizationKey: z.string().nullish(),
    prices: z.array(z.number()).nullish(),
    discounts: z.array(z.number()).nullish(),
    itemCounts: z.array(z.number()).nullish(),
    itemsToVerify: z.number().nullish(),
    unknownBarcodeItems: z.array(UnknownBarcodeItem).nullish(),
    bottleBundleChecks: BottleBundleCheckItem.array().nullish(),
    imageDetection: ImageDetectionItem.nullish(),
    imageDetections: ImageDetectionsItem.array().nullish(),
    wasSelectedRandomly: z.boolean().nullish(),
    weightWithoutScan: WeightWithoutScanItem.nullish(),
    riskAreas: RiskAreasItem.nullish(),
});

export const VerificationItem = z.object({
    verificationItemGuid: z.string(),
    referenceBasketItemGuids: z.array(z.string()).nullish(),
    verificationType: z.union([z.number(), z.string()]),
    properties: VerificationItemProperties.nullish(),
    approved: z.boolean().nullish(),
    createdOn: z.coerce.date().nullish(),
    approvedOn: z.coerce.date().nullish(),
    approvedBySource: z.string().nullish(),
    isOptional: z.boolean().nullish(),
    isDisabled: z.boolean().nullish(),
    isCardPaymentRequired: z.boolean().nullish(),
    source: zodFallback(
        z.enum([
            'IMAGE_DETECTION',
            'RANDOM',
            'SESSION_MONITOR',
            'CUSTOMER_MARK',
            'REMOVED_PRODUCTS',
            'WEIGHT_WITHOUT_SCAN',
            'MULTIPLE_SESSIONS_PER_DAY',
            'SCANNED_PRODUCTS',
            'SYSTEM_CUSTOMER_MARK',
            'RISK_AREA',
        ]),
        'unknown',
    ).nullish(),
});
